import React, { useState, useEffect } from 'react';
import { StaticQuery, graphql } from 'gatsby';
import { Header as BaseHeader } from '@dls/web';
import {
  setUrlParams,
  initializeSiteSearch360,
  siteSearch360InputClassName,
} from '@wec-core/helpers';
import { HeaderProps } from './Header.types';
import {
  accountLoginPath,
  detoxShopPath,
  logoutPath,
  onePassLogoutUrl,
} from '../../config/links';
import { translation as t } from '../../helpers/translation';
import IconLogo from '../../../assets/images/logo.png';
import IconProfile from '../../../assets/images/login.svg';
import IconProfileMobile from '../../../assets/images/mobile-login.svg';
import IconShoppingCartMobile from '../../../assets/images/mobile-shop.svg';
import IconShoppingCartDesktop from '../../../assets/images/shop.svg';
import IconShoppingCartLoggedInDesktop from '../../../assets/images/shop-logged-in.svg';
import IconHamburger from '../../../assets/images/hamburger-line.svg';

export const PureHeader = ({ data, baseUrl, isUserLoggedIn }: HeaderProps) => {
  const { header } = data;
  const [menuItems, setMenuItems] = useState(header.menuItems);
  const [firstLevelLinks, setFirstLevelLinks] = useState(
    header.firstLevelLinks
  );
  const [avatarLoggedInSubMenuItems, setAvatarLoggedInSubMenuItems] = useState(
    []
  );

  const logo = { ...header.logo, src: IconLogo, href: header.logo.link };

  useEffect(() => {
    const avatarMenuItems = [...header.avatarMenuItems];

    const callbackUrl = `${baseUrl}${header.loginButtonUrl}`;
    const onePassLogoutUrlWithParams = setUrlParams(onePassLogoutUrl, {
      success_callback_url: callbackUrl,
    });

    const hasLogoutButton = avatarMenuItems.find(
      item => item.title === header.logoutButtonText
    );

    // The header datamodel and component does not include a log out button so we append
    // the logout object and give it functionality
    if (!hasLogoutButton) {
      avatarMenuItems.push({
        title: header.logoutButtonText,
        href: logoutPath, // This href does nothing but provide a vanity url if user hovers over the anchor tag
        onClick: e => {
          e.preventDefault();
          window.location.href = onePassLogoutUrlWithParams;
        },
      });
    }
    setAvatarLoggedInSubMenuItems(avatarMenuItems);
  }, [
    baseUrl,
    header.avatarMenuItems,
    header.loginButtonUrl,
    header.logoutButtonText,
  ]);

  const menuIcons = [
    {
      icon: {
        desktop: IconProfile,
        mobile: IconProfileMobile,
        href: !isUserLoggedIn ? accountLoginPath : null,
      },
      subMenu: isUserLoggedIn ? avatarLoggedInSubMenuItems : null,
    },
    {
      icon: {
        desktop: isUserLoggedIn
          ? IconShoppingCartLoggedInDesktop
          : IconShoppingCartDesktop,
        mobile: IconShoppingCartMobile,
        href: detoxShopPath,
      },
    },
  ];

  useEffect(() => {
    const menuItems = [...header.menuItems];

    // Set 1st menu item as default selection
    if (menuItems && menuItems[0] && menuItems[0].item) {
      menuItems[0].item.active = true;
    }
    setMenuItems(menuItems);
  }, [header.menuItems]);

  useEffect(() => {
    const firstLevelLinks = [...header.firstLevelLinks];

    // Set 1st first level link as default selection
    if (firstLevelLinks && firstLevelLinks[0]) {
      firstLevelLinks[0].active = true;
    }
    setFirstLevelLinks(firstLevelLinks);
  }, [header.firstLevelLinks]);

  const onSearchInputActivated = () => {
    initializeSiteSearch360();
  };

  return (
    <>
      <BaseHeader
        logo={logo}
        firstLevelLinks={firstLevelLinks}
        menuItems={menuItems}
        menuIcons={menuIcons}
        baseUrl={baseUrl}
        showHamburger={true}
        hamburgerOpenIcon={IconHamburger}
        showLogoHamburger={true}
        backgroundColor={'#FFF'}
        searchConfig={{
          onSearchInputActivated,
          searchInputClassName: siteSearch360InputClassName,
          placeholder: t('SEARCH'),
        }}
      />
    </>
  );
};

export const Header = props => {
  return (
    <StaticQuery
      query={graphql`
        {
          header {
            menuItems: headerList {
              item: linkItem {
                href: src
                title
              }
              subMenu {
                subMenu: menuList {
                  item: linkItem {
                    subMenu: menuList {
                      item: linkItem {
                        title
                        href: src
                      }
                    }
                    href: src
                    title
                  }
                }
                item: menuSectionTitle {
                  title
                  href: src
                }
              }
            }
            firstLevelLinks: linkItemList {
              title
              href: src
            }
            avatarMenuItems: links {
              title
              href: src
            }
            logo {
              link
              alt
              href: src
            }
            loginButtonUrl
            logoutButtonText
          }
        }
      `}
      render={data => <PureHeader data={data} {...props} />}
    />
  );
};

export default Header;
